import React from 'react'
import styled from 'styled-components'
import { Row, Col, Input, Select, Modal } from 'antd'
import { useTranslation } from 'react-i18next'
import BlockElement from '../../../../components/BlockElement'
import {
  MinusCircleTwoTone,
  ExclamationCircleOutlined,
  CaretUpFilled,
  CaretDownFilled,
  CheckOutlined,
  CloseOutlined,
} from '@ant-design/icons'
import { minusIconStyle, arrowIconStyle } from '../styles'
import { switchElements, disabledMove } from '../utils'
import { openErrorPopup } from '../../../../components/ConfirmationPopups'

interface Props {
  society: string
  chart: any
  setChart: (value: any) => void
}

const ChartSociety = (props: Props): JSX.Element => {
  const { t } = useTranslation()

  const { Option } = Select
  const { confirm } = Modal

  const showConfirmRemoveSociety = () => {
    confirm({
      title: `Suppression ${props.society}`,
      icon: <ExclamationCircleOutlined />,
      content: t(
        'ficheComparative.comparativeChart.popups.removeSocietyContent'
      ),
      okText: t('ficheComparative.comparativeChart.popups.ok'),
      okType: 'danger',
      cancelText: t('ficheComparative.comparativeChart.popups.cancel'),
      onOk() {
        removeSociety()
      },
    })
  }

  const showSameSocietyNameError = () => {
    openErrorPopup(
      t('ficheComparative.errorTitle'),
      t('ficheComparative.errorMessages.sameSocietyName')
    )
  }

  const reorderSocieties = (move: 'up' | 'down') => {
    if (societyDisabledMove(move)) return

    const societies = [...props.chart.societies]
    switchElements(societies, props.society, move)

    props.setChart((chart: any) => {
      return {
        ...chart,
        societies: societies,
      }
    })
  }

  const societyDisabledMove = (move: 'up' | 'down') => {
    if (props.society === 'SeDomicilier') return true
    return disabledMove(props.chart.societies, props.society, move, 1)
  }

  const updateSocietyName = (e: React.ChangeEvent) => {
    const newName = (e.target as HTMLInputElement).value
    const index = props.chart.societies.indexOf(props.society)
    const newSocieties = [...props.chart.societies]

    newSocieties[index] = newName

    // prevent duplicate names
    const sameNameCount = newSocieties.filter(
      (name: string) => name === newName
    ).length
    if (sameNameCount > 1) {
      showSameSocietyNameError()
      return
    }

    let updatedObject: any = {}
    if (props.chart.values[props.society] === undefined) {
      updatedObject = { ...props.chart.values }
      updatedObject[props.society] = {}
    } else {
      const values = props.chart.values
      for (const key in values) {
        if (key === props.society) {
          updatedObject[newName] = values[key]
        } else {
          updatedObject[key] = values[key]
        }
      }
    }

    props.setChart((chart: any) => {
      return {
        ...chart,
        societies: newSocieties,
        values: updatedObject,
      }
    })
  }

  const updateValue = (
    mainElement: string,
    subElement: string,
    type: string,
    value?: string
  ) => {
    props.setChart((chart: any) => {
      return {
        ...chart,
        values: {
          ...chart.values,
          [props.society]: {
            ...chart.values[props.society],
            [mainElement]: {
              ...chart.values[props.society]?.[mainElement],
              [subElement]: {
                type: type,
                value: value,
              },
            },
          },
        },
      }
    })
  }

  const changeType =
    (mainElement: string, subElement: string) => (value: string) => {
      if (value === 'text') updateValue(mainElement, subElement, value, '')
      else if (value === 'true')
        updateValue(mainElement, subElement, 'boolean', 'true')
      else if (value === 'false')
        updateValue(mainElement, subElement, 'boolean', 'false')
      else {
        updateValue(mainElement, subElement, 'none')
      }
    }

  const getType = (elementName: string, subElementName: string) => {
    const type =
      props.chart.values[props.society]?.[elementName]?.[subElementName]?.type
    if (type !== 'boolean') return type

    return props.chart.values[props.society]?.[elementName]?.[subElementName]
      ?.value
  }

  const removeSociety = () => {
    props.setChart((chart: any) => {
      const { ...newValues } = chart.values

      delete newValues[Object.values(newValues).indexOf(props.society)]

      return {
        ...chart,
        societies: chart.societies.filter(
          (item: any) => item !== props.society
        ),
        values: newValues,
      }
    })
  }

  return (
    <BlockElement>
      <Row gutter={[5, 20]}>
        <Col span={23}>
          <Input
            key={`input.${props.society}`}
            defaultValue={props.society}
            disabled={props.society === 'SeDomicilier'}
            onBlur={updateSocietyName}
          />
          <Wrapper>
            {props.chart.structure.elements.map(
              (elementName: any, index: number) => {
                return (
                  <Row gutter={[5, 20]} key={`${index}.${elementName}`}>
                    <Col span={4}>
                      <span>{elementName}</span>
                    </Col>
                    <Col span={20}>
                      {props.chart.structure.hierarchy[elementName]?.map(
                        (subElementName: string, index: number) => {
                          return (
                            <Row gutter={[5, 5]} key={index}>
                              <Col span={4}>
                                <span>{subElementName}</span>
                              </Col>
                              <Col span={4}>
                                <Select
                                  defaultValue={
                                    getType(elementName, subElementName)
                                      ? getType(elementName, subElementName)
                                      : 'none'
                                  }
                                  style={{ width: '90%' }}
                                  onChange={changeType(
                                    elementName,
                                    subElementName
                                  )}
                                >
                                  <Option value="none">none</Option>
                                  <Option value="text">Texte</Option>
                                  <Option value="true">
                                    <CheckOutlined />
                                  </Option>
                                  <Option value="false">
                                    <CloseOutlined />
                                  </Option>
                                </Select>
                              </Col>
                              <Col span={16}>
                                {props.chart.values[props.society]?.[
                                  elementName
                                ]?.[subElementName]?.type === 'text' && (
                                  <Input
                                    key={`input.${props.society}.${subElementName}`}
                                    defaultValue={
                                      props.chart.values[props.society]?.[
                                        elementName
                                      ]?.[subElementName]?.value
                                    }
                                    onBlur={e =>
                                      updateValue(
                                        elementName,
                                        subElementName,
                                        'text',
                                        e.target.value
                                      )
                                    }
                                  />
                                )}
                              </Col>
                              <Col span={1}></Col>
                            </Row>
                          )
                        }
                      )}
                    </Col>
                  </Row>
                )
              }
            )}
          </Wrapper>
        </Col>
        <Col span={1}>
          <Row gutter={[6, 0]}>
            <Col>
              <Row>
                <CaretUpFilled
                  style={arrowIconStyle(societyDisabledMove('up'))}
                  onMouseDown={() => reorderSocieties('up')}
                />
              </Row>
              <Row>
                <CaretDownFilled
                  style={arrowIconStyle(societyDisabledMove('down'))}
                  onMouseDown={() => reorderSocieties('down')}
                />
              </Row>
            </Col>
            <Col>
              <MinusCircleTwoTone
                key={`delete.${props.society}`}
                style={minusIconStyle}
                onMouseDown={() => showConfirmRemoveSociety()}
              />
            </Col>
          </Row>
        </Col>
      </Row>
    </BlockElement>
  )
}

const Wrapper = styled.div`
  margin-top: 20px;
`

export default ChartSociety
